import { window } from "global";
import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from "react";
import classnames from "classnames";
import { UpdateStateContext, UpdateDispatchContext } from "components/SummonerProfiles/SummonerProfileUpdateReducer";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";
import MediaQuery from "components/MediaQuery";
import MatchFilters from "./MatchFilters";
import MatchStats from "./MatchStats";
import LargeMatchCard from "components/SummonerProfiles/views/ProfileMatchCard/LargeMatchCard";
import { useSummonerProfile, useSummonerRankSnapshots } from "api/summonerProfileApi";
import {
  getParamsForFetchMatchSummaryQuery,
  useFetchSummonerMatchSummaries,
  useFetchSummonerMatchSummariesLazily,
} from "api/matchSummaryApi";
import { useMatchLPs, useNewMatches } from "./match-history-helpers";
import { QUEUE_TYPE_MAP } from "./match-history-filters";
import { defaultOverviewFilters } from "components/SummonerProfiles/views/ProfileOverview/overview-filters";
import { queueTypeMapping } from "api/apiHelpers";
import { logUpdateNewMatchesCount } from "../overview-logging";
import { CSSTransition, Transition } from "react-transition-group";

const defaultFilters = {
  queueType: defaultOverviewFilters.matchHistory.queueType,
  championId: defaultOverviewFilters.matchHistory.championId,
  duoName: defaultOverviewFilters.matchHistory.duoName,
};

const MatchBlock = (props) => {
  const [mounted, setMounted] = useState(false);
  const dispatch = useContext(UpdateDispatchContext);
  const { isUpdatingProfile } = useContext(UpdateStateContext);
  const context = getSummonerProfileContext();
  const { summonerName, region, isSameSummoner, userVerified } = context;
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);
  const apiParams = {
    queueType: QUEUE_TYPE_MAP[filters.queueType],
    championId: filters.championId,
    duoName: filters.duoName,
  };

  const { loading: loadingProfileInit, data: profileInitData } = useSummonerProfile(region, summonerName, {}, null);

  const {
    data: rankSnapshots,
    loading: loadingRankSnapshots,
    error: errorRankSnapshots,
    networkStatus: networkStatusRankSnapshots,
    refetch: refetchRankSnapshots,
    variables: variablesRankSnapshots,
  } = useSummonerRankSnapshots(region, summonerName, {
    ssr: false,
    notifyOnNetworkStatusChange: true,
  });
  const { getSummonerRankSnapshots } = rankSnapshots || {};

  const clientLoadingMatchSummaryPage = useRef(false);
  const [matchSummaryPage, setMatchSummaryPage] = useState(1);

  const {
    loading: matchSummariesLoading,
    data: matchSummariesData,
    error: matchSummariesError,
    refetch: matchSummariesRefetch,
    variables: matchSummariesVariables,
  } = useFetchSummonerMatchSummaries(region, summonerName, apiParams, {
    ssr: false,
    fetchPolicy: !filtersChanged ? "cache-first" : "network-only",
    skip: matchSummaryPage !== 1 || clientLoadingMatchSummaryPage.current,
  });

  useEffect(() => {
    setFilters(defaultFilters);
  }, [summonerName, region]);

  useEffect(() => {
    dispatch({
      type: "addUpdates",
      payload: {
        matchSummary: matchSummariesRefetch,
        rankSnapshots: refetchRankSnapshots,
      },
    });
  }, [
    JSON.stringify(getParamsForFetchMatchSummaryQuery(region, summonerName, apiParams)),
    JSON.stringify(variablesRankSnapshots),
  ]);

  const { fetchPlayerMatchSummaries } = matchSummariesData || {};
  const { finishedMatchSummaries, matchSummaries, totalNumMatches } = fetchPlayerMatchSummaries || {};
  const matchSummaryPageRef = useRef(null);
  const [finishedGettingMatchSummaries, setFinishedGettingMatchSummaries] = useState(finishedMatchSummaries);
  const [matchSummaryPages, setMatchSummaryPages] = useState(matchSummaries ? [matchSummaries] : []);
  const [fetchSummonerMatchSummaries, { loading: loadingMatchPage, data: matchPage, error }] =
    useFetchSummonerMatchSummariesLazily(region, summonerName, apiParams);

  const { refetchedMatches, setMatchSummariesRPW } = props;
  const [updatingMatches, setUpdatingMatches] = useState(false);
  const [prevMatchIds, setPrevMatchIds] = useState([]);
  const [newMatchIds, setNewMatchIds] = useState([]);

  useEffect(() => {
    if (isUpdatingProfile && !updatingMatches) {
      // refetching
      // Prepare for new matches
      setUpdatingMatches(true);
      const prevMatchIds = (matchSummaryPages[0] || []).map((match) => match.matchId);
      setPrevMatchIds(prevMatchIds);
    }
    if (refetchedMatches && !isUpdatingProfile && updatingMatches) {
      const curMatchIds = (refetchedMatches || []).map((match) => match.matchId);
      const newMatchesDiff = curMatchIds.filter((matchId) => {
        return !prevMatchIds.includes(matchId);
      });
      setUpdatingMatches(false);
      setNewMatchIds(newMatchesDiff);
      logUpdateNewMatchesCount(newMatchesDiff);
    }
  }, [refetchedMatches, isUpdatingProfile, updatingMatches]);

  const allowPaginationScroll = useRef(matchSummariesLoading);
  const onLoadScroll = (e) => {
    if (allowPaginationScroll.current && matchSummaryPageRef && matchSummaryPageRef.current) {
      const { bottom } = matchSummaryPageRef.current.getBoundingClientRect();

      if (
        !loadingMatchPage &&
        !clientLoadingMatchSummaryPage.current &&
        !finishedGettingMatchSummaries &&
        window.innerHeight - bottom >= -100
      ) {
        clientLoadingMatchSummaryPage.current = true;
        fetchSummonerMatchSummaries(matchSummaryPage + 1);
      }
    }
  };

  useEffect(() => {
    allowPaginationScroll.current = !matchSummariesLoading;
  }, [matchSummariesLoading]);

  const onFiltersChanged = useCallback(() => {
    setFiltersChanged(true);
    setMatchSummaryPage(1);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onLoadScroll);

    return () => {
      window.removeEventListener("scroll", onLoadScroll);
    };
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setMatchSummaryPage(1);
    setPrevMatchIds([]);
    setNewMatchIds([]);
    setUpdatingMatches(false);
    clientLoadingMatchSummaryPage.current = false;
  }, [summonerName, region, JSON.stringify(getParamsForFetchMatchSummaryQuery(region, summonerName, apiParams))]);

  useEffect(() => {
    if (matchSummaries && !matchSummariesLoading && !updatingMatches && !isUpdatingProfile) {
      setMatchSummaryPage(1);
      setMatchSummaryPages(matchSummaries ? [matchSummaries] : []);
      setMatchSummariesRPW(matchSummaries ? [matchSummaries] : []);
      setFinishedGettingMatchSummaries(finishedMatchSummaries);
      clientLoadingMatchSummaryPage.current = false;
    }
  }, [matchSummaries, matchSummariesLoading, updatingMatches, isUpdatingProfile]);

  useEffect(() => {
    if (!loadingMatchPage && clientLoadingMatchSummaryPage.current) {
      clientLoadingMatchSummaryPage.current = false;
      setMatchSummaryPage(matchSummaryPage + 1);
      if (matchPage && matchPage.matchSummaries) {
        setFinishedGettingMatchSummaries(matchPage.finishedMatchSummaries);
        setMatchSummaryPages([...matchSummaryPages, matchPage.matchSummaries]);
        setMatchSummariesRPW([...matchSummaryPages, matchPage.matchSummaries]);
      }
    }
  }, [loadingMatchPage]);

  const completeList = matchSummaryPages.reduce((acc, cur) => {
    cur.forEach((m) => {
      // only push if the match is not already in the complete list
      if (acc.some((am) => am.matchId == m.matchId) == false) {
        acc.push(m);
      }
    });
    return acc;
  }, []);
  const matchLPs = useMatchLPs(completeList, getSummonerRankSnapshots);
  let content = null;
  if (networkStatusRankSnapshots === 1 || matchSummariesLoading) {
    content = (
      <div className="match-history_loading">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  } else if (completeList.length !== 0) {
    content = (
      <React.Fragment>
        {completeList.map((match, index) => {
          const isNew = newMatchIds.includes(match.matchId);
          const matchProps = {
            // key: match.matchId,
            data: match,
            isNew,
            matchLP: matchLPs[match.matchId],
            // matchLP: index === 0 ? {lp: 14, promoProgress: undefined} : {lp: undefined, promoProgress: "WLLWN"},
            observable: index > 9 || (matchSummaryPages && matchSummaryPages[0] && matchSummaryPages[0].length <= index),
          };

          return !isNew ? (
            <div key={index} className={"match-history_match-card"}>
              <LargeMatchCard {...matchProps} />
            </div>
          ) : (
            <Transition key={index} timeout={0} in appear={mounted}>
              {(state) => (
                <div className={`match-history_match-card match-history_match-card__${state}`}>
                  <LargeMatchCard {...matchProps} />
                </div>
              )}
            </Transition>
          );
        })}
        {(loadingMatchPage || clientLoadingMatchSummaryPage.current) && (
          <div className="spinthatshit-loader load-more">
            <div className="spinner"></div>
          </div>
        )}
      </React.Fragment>
    );
  } else {
    content = (
      <div className="match-history_empty">
        No games for current filters. Try changing the filters or playing some more matches.
      </div>
    );
  }

  return (
    <div className="content-section content-section_no-padding match-block" ref={matchSummaryPageRef}>
      <div className="match-block_header">
        <div className="description">
          <div className="bluebar" />
          <span>Match History</span>
        </div>
        <MatchFilters filters={filters} setFilters={setFilters} onFiltersChanged={onFiltersChanged} />
      </div>
      <MatchStats matchSummaryPages={matchSummaryPages} summonerName={summonerName} />
      {content}
    </div>
  );
};

export default MatchBlock;

// MatchBlock.whyDidYouRender = true;
